import type { FC } from "react"
import type { CommentResponse } from "@/types/domain"
import { formatDistanceToNow } from "date-fns"
import { ko } from "date-fns/locale"
import { Heart, MessageCircle } from "lucide-react"
import { useAppDispatch } from "@/app/hooks"
import { toggleLikeComment } from "@/features/postDetail/postDetailSlice"

type CommentItemProps = {
  comment: CommentResponse
  onReply: (comment: CommentResponse) => void
  depth?: number
}

const CommentItem: FC<CommentItemProps> = ({ comment, onReply, depth = 0 }) => {
  const dispatch = useAppDispatch()
  
  const handleLike = () => {
    void dispatch(toggleLikeComment(comment.id))
  }

  const timeAgo = formatDistanceToNow(new Date(comment.createdAt), {
    addSuffix: true,
    locale: ko,
  })

  // 대댓글 깊이에 따른 들여쓰기 (최대 2단계까지만 UI적으로 구분하고 그 이후는 플랫하거나 동일하게)
  const paddingLeft = depth > 0 ? `${(Math.min(depth, 1) * 20).toString()}px` : "0"

  return (
    <div className={`comment-item ${depth > 0 ? "reply" : ""}`} style={{ paddingLeft }}>
      <div className="comment-content-wrapper">
        <img
          src={comment.author.profileImageUrl ?? "https://via.placeholder.com/32"}
          alt={comment.author.nickname}
          className="comment-avatar"
        />
        <div className="comment-main">
          <div className="comment-header">
            <span className="comment-author">{comment.author.nickname}</span>
            <span className="comment-time">{timeAgo}</span>
          </div>
          
          <p className="comment-text">
            {comment.content}
          </p>
          
          <div className="comment-actions">
            <button 
              onClick={handleLike}
              className={`comment-action-btn ${comment.liked ? "active" : ""}`}
            >
              <Heart className={`w-3.5 h-3.5 ${comment.liked ? "fill-current" : ""}`} />
              <span>{comment.likeCount > 0 ? comment.likeCount : "좋아요"}</span>
            </button>
            <button 
              onClick={() => { onReply(comment) }}
              className="comment-action-btn"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              <span>답글달기</span>
            </button>
          </div>
        </div>
      </div>

      {/* Render children/replies */}
      {comment.children.length > 0 && (
        <div className="replies-list">
          {comment.children.map(child => (
             <CommentItem 
               key={child.id} 
               comment={child} 
               onReply={onReply} 
               depth={depth + 1} 
             />
          ))}
        </div>
      )}
    </div>
  )
}

export default CommentItem
